<app-loading *ngIf="contentReady"></app-loading>

<section class="bb-page">
  <div class="bb-page-shell">

    <header class="bb-page-header">
      <div class="bb-page-title-block">
                <span class="bb-eyebrow">{{ 'add stock' | transloco }}</span>
        <h2 class="bb-page-title">{{ "add stock" | transloco }}</h2>
      </div>
    </header>

    <div class="bb-card bb-p-20">
      <form [formGroup]="data" (ngSubmit)="onSubmit()">
        <div class="bb-form-grid">

          @if(submitted && data.invalid) {
          <div class="bb-form-alert bb-field-full" role="alert">
            {{ 'Review the required fields before saving' | transloco }}
            @if(getMissingRequiredCount() > 0) {
            <span>
              ({{ getMissingRequiredCount() }} {{ 'required fields' | transloco }})
            </span>
            }
          </div>
          }

          @if(isAdmin){
          <div class="bb-field bb-field-full">
            <label class="bb-label" for="warehouse">{{ 'select' | transloco }} {{ 'Stock' | transloco }}</label>
            <div class="bb-select-wrap">
              <select id="warehouse" class="bb-select" formControlName="warehouse"
                (change)="onWarehouseChange($any($event.target).value)" [ngClass]="{
                'is-invalid': data.get('warehouse')?.touched && data.get('warehouse')?.invalid,
                'is-valid': data.get('warehouse')?.touched && data.get('warehouse')?.valid
              }">
                <option value="" disabled>{{ 'select' | transloco }}</option>
                @for (warehouse of warehouses; track warehouse) {
                <option [value]="warehouse.id">{{ warehouse.name }}</option>
                }
              </select>
            </div>
            @if(shouldShowError('warehouse')) {
            <div class="bb-field-error">{{ getErrorMessage('warehouse', ('Stock' | transloco)) }}</div>
            }
          </div>
          }

          @if(!isUpdatePage && !isClone) {
          <div class="bb-field bb-field-full">
            <label class="bb-label">{{ 'Product add mode' | transloco }}</label>
            <div class="bb-additem-mode-switch" role="group">
              <button type="button" class="bb-additem-mode-btn" [class.active]="addMode === 'new'"
                (click)="setAddMode('new')">
                <i class="fa-solid fa-plus"></i>
                <span>{{ 'Create new product' | transloco }}</span>
              </button>
              <button type="button" class="bb-additem-mode-btn" [class.active]="addMode === 'existing'"
                (click)="setAddMode('existing')">
                <i class="fa-solid fa-box-open"></i>
                <span>{{ 'Choose existing product' | transloco }}</span>
              </button>
            </div>
          </div>
          }

          @if(addMode === 'existing' && !isUpdatePage) {
          <div class="bb-field bb-field-full bb-existing-products-field">
            <div class="bb-flex bb-items-center bb-justify-between bb-gap-12 bb-wrap bb-mb-12">
              <label class="bb-label">{{ 'select product' | transloco }}</label>
            </div>

            <div class="bb-field bb-field-full bb-mb-12">
              <label class="bb-label">{{ 'Category' | transloco }}</label>
              <div class="bb-additem-category-card" [dir]="isAr ? 'rtl' : 'ltr'">
                <div class="bb-additem-category-list" role="radiogroup">
                  @for (category of systemProductCategories; track category.id) {
                  <button type="button" class="bb-additem-category-chip"
                    [class.active]="isSystemProductsCategorySelected(category.id)"
                    (click)="onSystemProductsCategoryChange(category.id)">
                    {{ category.label | transloco }}
                  </button>
                  }
                </div>
              </div>
            </div>

            <input type="hidden" formControlName="system_product_id" />

            <button type="button" class="bb-additem-products-trigger" [class.open]="productsDropdownOpen"
              (click)="toggleProductsDropdown()">
              <span>
                @if(getSelectedProductsCount() > 0) {
                {{ getSelectedProductsCount() }} {{ 'Selected Products' | transloco }}
                } @else {
                {{ 'choose products' | transloco }}
                }
              </span>
              <i class="fa-solid fa-chevron-down"></i>
            </button>

            @if(productsDropdownOpen) {
            <div class="bb-additem-products-dropdown">
            <div class="bb-additem-product-search">
              <i class="fa-solid fa-magnifying-glass"></i>
              <input class="bb-input" type="search"
                [(ngModel)]="systemProductsSearch"
                [ngModelOptions]="{standalone: true}"
                (ngModelChange)="onSystemProductsSearchChange()"
                placeholder="{{ 'Search products by name barcode or tags' | transloco }}" />
            </div>

            <div class="bb-additem-dropdown-actions">
              <button type="button" class="bb-btn bb-btn-secondary bb-additem-select-all"
                (click)="toggleAllSystemProducts()" [disabled]="!systemProductsCategoryId || loadingSystemProducts || availableSystemProducts.length === 0">
                <i class="fa-solid" [ngClass]="selectingAllSystemProducts ? 'fa-spinner fa-spin' : (areAllSystemProductsSelected() ? 'fa-square-check' : 'fa-list-check')"></i>
                <span>
                  {{ (selectingAllSystemProducts ? 'Loading products...' : (areAllSystemProductsSelected() ? 'Clear selection' : 'Select all products')) | transloco }}
                </span>
              </button>
            </div>

            @if(loadingSystemProducts) {
            <div class="bb-field-hint">{{ 'Loading products...' | transloco }}</div>
            }

            @if(!loadingSystemProducts && displayedSystemProducts.length > 0) {
            <div class="bb-additem-products-list" [dir]="isAr ? 'rtl' : 'ltr'" (scroll)="onSystemProductsScroll($event)">
              @for (product of displayedSystemProducts; track product.id) {
              <label class="bb-additem-product-row" [class.selected]="isSystemProductSelected(product)">
                <input type="checkbox" class="bb-additem-product-check"
                  [checked]="isSystemProductSelected(product)" (change)="toggleSystemProduct(product)" />

                <img class="bb-additem-product-image" [src]="getProductImage(product)"
                  (error)="onProductImageError($event)" [alt]="getProductDisplayName(product)" />

                <span class="bb-additem-product-main">
                  <strong>{{ getProductDisplayName(product) }}</strong>
                  <small>{{ product.barcode || '-' }}</small>
                </span>

                <span class="bb-additem-product-meta">
                  <span>{{ getCategoryName(product.category_id) | transloco }}</span>
                  <small>{{ getPetName(product.pet) | transloco }}</small>
                </span>

                <span class="bb-additem-product-meta">
                  <span>{{ product.weight || '-' }}</span>
                  <small>{{ getPetTypeName(product.pet_type) | transloco }}</small>
                </span>

              </label>
              }

              @if(loadingMoreSystemProducts) {
              <div class="bb-additem-products-loading">
                {{ 'Loading products...' | transloco }}
              </div>
              }
            </div>
            }

            @if(!loadingSystemProducts && availableSystemProducts.length > 0 && displayedSystemProducts.length === 0) {
            <div class="bb-field-hint">{{ 'No products match your search' | transloco }}</div>
            }
            </div>
            }

            @if(shouldShowError('system_product_id')) {
            <div class="bb-field-error">{{ getErrorMessage('system_product_id', ('product' | transloco)) }}</div>
            }
            @if(!loadingSystemProducts && warehouseId && availableSystemProducts.length === 0) {
            <div class="bb-field-hint">
              {{ 'All system products already exist in this warehouse, or no products are available.' | transloco }}
            </div>
            }
          </div>

          @if(getSelectedProductsCount() > 0) {
          <div class="bb-field bb-field-full">
            <div class="bb-additem-product-summary">
              <i class="fa-solid fa-circle-check"></i>
              <span>{{ getSelectedProductsCount() }} {{ 'Selected Products' | transloco }}</span>
            </div>
          </div>
          }
          }

          @if(addMode === 'new' || isUpdatePage || isClone) {
          <div class="bb-field">
            <label class="bb-label" for="name_en">{{ 'Name' | transloco }}</label>
            <input class="bb-input" type="text" id="name_en" formControlName="name_en"
              placeholder="{{ 'Name' | transloco }}" [(ngModel)]="name_en" [ngClass]="{
                'is-invalid': data.get('name_en')?.touched && data.get('name_en')?.invalid,
                'is-valid': data.get('name_en')?.touched && data.get('name_en')?.valid
              }" />
            @if(shouldShowError('name_en')) {
            <div class="bb-field-error">{{ getErrorMessage('name_en', ('Name' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="name_ar">{{ 'NameAr' | transloco }}</label>
            <input class="bb-input" type="text" id="name_ar" formControlName="name_ar"
              placeholder="{{ 'NameAr' | transloco }}" [(ngModel)]="name_ar" [ngClass]="{
                'is-invalid': data.get('name_ar')?.touched && data.get('name_ar')?.invalid,
                'is-valid': data.get('name_ar')?.touched && data.get('name_ar')?.valid
              }" />
            @if(shouldShowError('name_ar')) {
            <div class="bb-field-error">{{ getErrorMessage('name_ar', ('NameAr' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="product_info_ar">{{ 'Description ar' | transloco }}</label>
            <input class="bb-input" type="text" id="product_info_ar" formControlName="product_info_ar"
              placeholder="{{ 'Description ar' | transloco }}" [(ngModel)]="product_info_ar" [ngClass]="{
                'is-invalid': data.get('product_info_ar')?.touched && data.get('product_info_ar')?.invalid,
                'is-valid': data.get('product_info_ar')?.touched && data.get('product_info_ar')?.valid
              }" />
          </div>

          <div class="bb-field">
            <label class="bb-label" for="product_info_en">{{ 'Description en' | transloco }}</label>
            <input class="bb-input" type="text" id="product_info_en" formControlName="product_info_en"
              placeholder="{{ 'Description en' | transloco }}" [(ngModel)]="product_info_en" [ngClass]="{
                'is-invalid': data.get('product_info_en')?.touched && data.get('product_info_en')?.invalid,
                'is-valid': data.get('product_info_en')?.touched && data.get('product_info_en')?.valid
              }" />
          </div>

          <div class="bb-field bb-field-full">
            <label class="bb-label" for="tags">{{ 'tags' | transloco }}</label>
            <input class="bb-input" type="text" id="tags" formControlName="tags" placeholder="{{ 'tags' | transloco }}"
              [(ngModel)]="tags" (blur)="addTags()" [ngClass]="{
                'is-invalid': data.get('tags')?.touched && data.get('tags')?.invalid,
                'is-valid': data.get('tags')?.touched && data.get('tags')?.valid
              }" />

            <div class="bb-inline-flex bb-wrap bb-gap-8 bb-mt-12">
              <span *ngFor="let tag of tagsArray" class="bb-chip-item">
                {{ tag }}
                <button type="button" class="bb-chip-remove-btn" (click)="removeTag(tag)">×</button>
              </span>
            </div>
          </div>
          }

          @if(shouldShowStockForm()) {
          <div class="bb-field">
            <label class="bb-label" for="quantity">{{ 'quantity' | transloco }}</label>
            <input class="bb-input" type="number" id="quantity" formControlName="quantity"
              placeholder="{{ 'quantity' | transloco }}" [(ngModel)]="quantity" [ngClass]="{
                'is-invalid': data.controls['quantity'].touched && data.controls['quantity'].errors,
                'is-valid': data.controls['quantity'].touched && !data.controls['quantity'].errors,
                'is-touched': data.controls['quantity'].untouched && !data.controls['quantity'].errors,
              }" />
            @if(shouldShowError('quantity')) {
            <div class="bb-field-error">{{ getErrorMessage('quantity', ('quantity' | transloco)) }}</div>
            }
          </div>

          @if(addMode === 'new' || isUpdatePage || isClone) {
          <div class="bb-field">
            <label class="bb-label" for="weight">{{ 'weight' | transloco }}</label>
            <input class="bb-input" type="text" id="weight" formControlName="weight"
              placeholder="{{ 'weight' | transloco }}" [(ngModel)]="weight" [ngClass]="{
                'is-invalid': data.controls['weight'].touched && data.controls['weight'].errors,
                'is-valid': data.controls['weight'].touched && !data.controls['weight'].errors,
                'is-touched': data.controls['weight'].untouched && !data.controls['weight'].errors,
              }" />
            @if(shouldShowError('weight')) {
            <div class="bb-field-error">{{ getErrorMessage('weight', ('weight' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label">{{ 'Category' | transloco }}</label>
            <div class="bb-select-wrap">
              <select class="bb-select" formControlName="category_id" [(ngModel)]="category_id" [ngClass]="{
                'is-invalid': data.get('category_id')?.touched && data.get('category_id')?.invalid,
                'is-valid': data.get('category_id')?.touched && data.get('category_id')?.valid
              }">
                <option value="">{{ 'select' | transloco }}</option>
                <option value="0"> {{ "soft food" | transloco }}</option>
                <option value="1"> {{ "soft food can" | transloco }}</option>
                <option value="2"> {{ "dry food" | transloco }}</option>
                <option value="3">{{ "bird food" | transloco }} </option>
                <option value="4"> {{ "medicine" | transloco }}</option>
                <option value="5"> {{ "supplement" | transloco }}</option>
                <option value="6"> {{ "accessories" | transloco }}</option>
                <option value="7"> {{ "toy" | transloco }}</option>
                <option value="8">{{ "grooming" | transloco }} </option>
                <option value="9"> {{ "house" | transloco }}</option>
                <option value="10"> {{ "care" | transloco }}</option>
                <option value="11">{{ "treats" | transloco }} </option>
                <option value="12"> {{ "training pads" | transloco }}</option>
                <option value="13">{{ "bags" | transloco }} </option>
                <option value="14"> {{ "cleaning tools" | transloco }}</option>
                <option value="15">{{ "litter" | transloco }} </option>
              </select>
            </div>
            @if(shouldShowError('category_id')) {
            <div class="bb-field-error">{{ getErrorMessage('category_id', ('Category' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label">{{ "pet" | transloco }}</label>
            <div class="bb-select-wrap">
              <select class="bb-select" formControlName="pet" [(ngModel)]="pet" [ngClass]="{
                'is-invalid': data.get('pet')?.touched && data.get('pet')?.invalid,
                'is-valid': data.get('pet')?.touched && data.get('pet')?.valid
              }">
                <option value="">{{ 'select' | transloco }}</option>
                <option value="0"> {{ "cats" | transloco }}</option>
                <option value="1"> {{ "dogs" | transloco }}</option>
                <option value="2"> {{ "birds" | transloco }}</option>
                <option value="4"> {{ "cats&dogs" | transloco }}</option>
                <option value="3">{{ "other" | transloco }} </option>
              </select>
            </div>
          </div>

          <div class="bb-field">
            <label class="bb-label">{{ 'pet type' | transloco }}</label>
            <div class="bb-select-wrap">
              <select class="bb-select" formControlName="pet_type" [(ngModel)]="pet_type" [ngClass]="{
                'is-invalid': data.get('pet_type')?.touched && data.get('pet_type')?.invalid,
                'is-valid': data.get('pet_type')?.touched && data.get('pet_type')?.valid
              }">
                <option value="">{{ 'select' | transloco }}</option>
                <option value="0"> {{ "adult" | transloco }}</option>
                <option value="1"> {{ "kitten" | transloco }}</option>
                <option value="2"> {{ "sterilized" | transloco }}</option>
                <option value="3"> {{ "persian" | transloco }}</option>
                <option value="4"> {{ "puppy" | transloco }}</option>
                <option value="5"> {{ "birds" | transloco }}</option>
                <option value="6">{{ "other" | transloco }} </option>
              </select>
            </div>
          </div>

          @if(warehouseTypeService.isBlueBoxMain){
          <div class="bb-field">
            <label class="bb-label">{{ "brand" | transloco }}</label>
            <div class="bb-select-wrap">
              <select class="bb-select" formControlName="brand_id" [(ngModel)]="brand_id" [ngClass]="{
                'is-invalid': data.get('brand_id')?.touched && data.get('brand_id')?.invalid,
                'is-valid': data.get('brand_id')?.touched && data.get('brand_id')?.valid
              }">
                <option value="">{{ "select brand" | transloco }} </option>
                <option *ngFor="let brand of brands" [value]="brand.id">
                  {{ brand.brandname_ar }} - {{ brand.brandname_en }}
                </option>
              </select>
            </div>
          </div>
          }

          @if(isUpdatePage){
          <div class="bb-field">
            <label class="bb-label" for="sku">{{ 'sku' | transloco }}</label>
            <input class="bb-input" type="text" id="sku" formControlName="sku" placeholder="{{ 'sku' | transloco }}"
              [(ngModel)]="sku" [ngClass]="{
                'is-invalid': data.controls['sku'].touched && data.controls['sku'].errors,
                'is-valid': data.controls['sku'].touched && !data.controls['sku'].errors,
                'is-touched': data.controls['sku'].untouched && !data.controls['sku'].errors,
              }" />
            @if(shouldShowError('sku')) {
            <div class="bb-field-error">{{ getErrorMessage('sku', ('sku' | transloco)) }}</div>
            }
          </div>
          }

          <div class="bb-field">
            <label class="bb-label" for="barcode">{{ 'barcode' | transloco }}</label>
            <input class="bb-input" type="text" id="barcode" formControlName="barcode"
              placeholder="{{ 'barcode' | transloco }}" [(ngModel)]="barcode" [ngClass]="{
                'is-invalid': data.controls['barcode'].touched && data.controls['barcode'].errors,
                'is-valid': data.controls['barcode'].touched && !data.controls['barcode'].errors,
                'is-touched': data.controls['barcode'].untouched && !data.controls['barcode'].errors,
              }" />
          </div>
          }

          <div class="bb-field">
            <label class="bb-label" for="price">{{ 'price' | transloco }}</label>
            <input class="bb-input" type="number" id="price" formControlName="price" placeholder="{{ 'price' | transloco }}"
              [(ngModel)]="price" (input)="onlyNumbers($event)" maxlength="6" [ngClass]="{
                'is-invalid': data.controls['price'].touched && data.controls['price'].errors,
                'is-valid': data.controls['price'].touched && !data.controls['price'].errors,
                'is-touched': data.controls['price'].untouched && !data.controls['price'].errors,
              }" />
            @if(shouldShowError('price')) {
            <div class="bb-field-error">{{ getErrorMessage('price', ('price' | transloco)) }}</div>
            }
          </div>

          @if(!this.warehouseIsStore){
          <div class="bb-field">
            <label class="bb-label" for="pricea">{{ 'Page A' | transloco }}</label>
            <input class="bb-input" type="number" id="pricea" formControlName="pricea" (input)="onlyNumbers($event)"
              maxlength="6" placeholder="{{ 'Page A' | transloco }}" [(ngModel)]="pricea" [ngClass]="{
                'is-invalid': data.controls['pricea'].touched && data.controls['pricea'].errors,
                'is-valid': data.controls['pricea'].touched && !data.controls['pricea'].errors,
                'is-touched': data.controls['pricea'].untouched && !data.controls['pricea'].errors,
              }" />
            @if(shouldShowError('pricea')) {
            <div class="bb-field-error">{{ getErrorMessage('pricea', ('Page A' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="priceb">{{ 'B' | transloco }}</label>
            <input class="bb-input" type="number" id="priceb" formControlName="priceb" placeholder="{{ 'B' | transloco }}"
              (input)="onlyNumbers($event)" maxlength="6" [(ngModel)]="priceb" [ngClass]="{
                'is-invalid': data.controls['priceb'].touched && data.controls['priceb'].errors,
                'is-valid': data.controls['priceb'].touched && !data.controls['priceb'].errors,
                'is-touched': data.controls['priceb'].untouched && !data.controls['priceb'].errors,
              }" />
            @if(shouldShowError('priceb')) {
            <div class="bb-field-error">{{ getErrorMessage('priceb', ('B' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="pricec">{{ 'C'| transloco }}</label>
            <input class="bb-input" type="number" id="pricec" formControlName="pricec" placeholder="{{ 'C'| transloco }}"
              (input)="onlyNumbers($event)" maxlength="6" [(ngModel)]="pricec" [ngClass]="{
                'is-invalid': data.controls['pricec'].touched && data.controls['pricec'].errors,
                'is-valid': data.controls['pricec'].touched && !data.controls['pricec'].errors,
                'is-touched': data.controls['pricec'].untouched && !data.controls['pricec'].errors,
              }" />
            @if(shouldShowError('pricec')) {
            <div class="bb-field-error">{{ getErrorMessage('pricec', ('C' | transloco)) }}</div>
            }
          </div>

          <div class="bb-field">
            <label class="bb-label" for="affiliationprice">{{ 'Affiliation' | transloco }}</label>
            <input class="bb-input" type="number" id="affiliationprice" formControlName="affiliationprice"
              (input)="onlyNumbers($event)" maxlength="6" placeholder="{{ 'Affiliation' | transloco }}"
              [(ngModel)]="affiliationprice" [ngClass]="{
                'is-invalid': data.controls['affiliationprice'].touched && data.controls['affiliationprice'].errors,
                'is-valid': data.controls['affiliationprice'].touched && !data.controls['affiliationprice'].errors,
                'is-touched': data.controls['affiliationprice'].untouched && !data.controls['affiliationprice'].errors,
              }" />
            @if(shouldShowError('affiliationprice')) {
            <div class="bb-field-error">{{ getErrorMessage('affiliationprice', ('Affiliation' | transloco)) }}</div>
            }
          </div>
          }

          <div class="bb-field">
            <label class="bb-label" for="expiration_data">{{ 'Exp Date' | transloco }}</label>

            <div class="bb-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
              <button
                type="button"
                class="bb-booking-calendar-trigger"
                id="expiration_data"
                (click)="toggleExpirationCalendar()"
                [ngClass]="{
                  'is-invalid': data.get('expiration_data')?.touched && data.get('expiration_data')?.invalid,
                  'is-valid': data.get('expiration_data')?.touched && data.get('expiration_data')?.valid
                }"
              >
                <span>{{ getExpirationDateDisplay() }}</span>
                <i class="fa-solid fa-calendar-days"></i>
              </button>

              @if(showExpirationCalendar) {
              <div class="bb-booking-calendar-panel">
                <div class="bb-booking-calendar-head">
                  <button type="button" (click)="previousExpirationCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                  </button>

                  <strong>{{ getExpirationCalendarTitle() }}</strong>

                  <button type="button" (click)="nextExpirationCalendarMonth()">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                  </button>
                </div>

                <div class="bb-booking-calendar-weekdays">
                  @for(dayName of getExpirationCalendarWeekDays(); track dayName) {
                  <span>{{ dayName }}</span>
                  }
                </div>

                <div class="bb-booking-calendar-grid">
                  @for(calendarDay of getExpirationCalendarDays(); track calendarDay.date.getTime()) {
                  <button
                    type="button"
                    class="bb-booking-calendar-day"
                    [class.other-month]="!calendarDay.isCurrentMonth"
                    [class.today]="calendarDay.isToday"
                    [class.selected]="calendarDay.isSelected"
                    [disabled]="calendarDay.isDisabled"
                    (click)="selectExpirationCalendarDate(calendarDay.date)"
                  >
                    {{ calendarDay.dayNumber }}
                  </button>
                  }
                </div>
              </div>
              }
            </div>

            <input type="hidden" formControlName="expiration_data" />

            @if(shouldShowError('expiration_data')) {
            <div class="bb-field-error">{{ getErrorMessage('expiration_data', ('Exp Date' | transloco)) }}</div>
            }
          </div>
          }

          <div class="bb-field bb-field-full bb-mt-16">
            @if(isUpdatePage) {
            <button type="submit" class="bb-btn bb-btn-primary bb-w-100" [class.is-disabled]="data.invalid">
              {{ "Edit" | transloco }}
            </button>
            }@else {
            <button type="submit" class="bb-btn bb-btn-primary bb-w-100" [class.is-disabled]="data.invalid">
              {{ "Submit" | transloco }}
            </button>
            }
          </div>

        </div>
      </form>
    </div>

  </div>
</section>
